/**
 * useFeatureFlags Hook
 *
 * A hook for accessing feature flags in components.
 *
 * Primary responsibilities:
 * - Provide access to feature flags via OpenFeature
 * - Allow overriding flags in development
 * - Track flag usage in analytics
 * - Handle initialization failures gracefully
 *
 * Dependencies:
 * - Jotai for override management
 * - OpenFeature for flag resolution
 */

import { useAtom } from 'jotai'
import { useCallback, useEffect, useState, useMemo } from 'react'
import {
  flagOverridesAtom,
  newUIComponentFlag,
  heroTextTestFlag,
  premiumFeatureFlag,
  FeatureFlags,
  HeroTextVariant
} from '@/atoms/featureFlagsAtoms'
import { defaultClient, trackFlagEvaluation } from '@/lib/openfeature/client'
import { Client } from '@openfeature/web-sdk'
import { isDev } from '@/lib/utils/isDev'

// Check if running on iOS device
const isIOSDevice =
  typeof navigator !== 'undefined' &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1))

// Check if this is a low-end device based on hardware
const isLowEndDevice =
  typeof navigator !== 'undefined' &&
  // @ts-ignore - deviceMemory is not in standard navigator type
  (('deviceMemory' in navigator && navigator.deviceMemory < 4) ||
    (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4))

// Flag results cache to prevent repeated API calls
const flagResultsCache: Record<
  string,
  {
    value: any
    timestamp: number
    expiry: number
  }
> = {}

// Cache timeout (ms) - how long to cache flag values
const CACHE_TIMEOUT = isIOSDevice || isLowEndDevice ? 60000 : 30000 // 1 minute for mobile, 30 seconds for desktop

// Function to track feature flag usage
async function trackFeatureFlag(
  flagKey: string,
  flagValue: boolean | string | number | object,
  event: string = 'impression',
  metadata?: Record<string, any>
) {
  try {
    const response = await fetch('/api/feature-flags/track', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        flagKey,
        flagValue,
        event,
        metadata
      })
    })

    if (!response.ok) {
      isDev && console.error(`Error tracking feature flag: ${await response.text()}`)
    }
  } catch (error) {
    // Silently fail - analytics should never break the app
  }
}

export function useFeatureFlags() {
  const [overrides, setOverrides] = useAtom(flagOverridesAtom)
  const [client, setClient] = useState<typeof defaultClient | null>(null)
  const [isClientReady, setIsClientReady] = useState(false)
  const [initializationFailed, setInitializationFailed] = useState(false)
  const [newUIFlag] = useAtom(newUIComponentFlag)
  const [heroTextFlag] = useAtom(heroTextTestFlag)
  const [premiumFlag] = useAtom(premiumFeatureFlag)
  const [, setNewUIFlag] = useAtom(newUIComponentFlag)
  const [, setHeroTextFlag] = useAtom(heroTextTestFlag)
  const [, setPremiumFlag] = useAtom(premiumFeatureFlag)

  // Initialize client
  useEffect(() => {
    // Only run on client side
    if (typeof window !== 'undefined') {
      try {
        setClient(defaultClient)
        // For iOS devices, set a timeout to mark client as ready even if it fails
        if (isIOSDevice || isLowEndDevice) {
          const timer = setTimeout(() => {
            setIsClientReady(true)
          }, 100) // Much shorter timeout - don't wait for too long
          return () => clearTimeout(timer)
        }
        setIsClientReady(true)
      } catch (error) {
        isDev && console.error('Error initializing feature flags client:', error)
        setInitializationFailed(true)
        setIsClientReady(true) // Still mark as ready to not block the app
      }
    }
  }, [])

  // Initialize feature flag atoms with their current values
  useEffect(() => {
    if (!client || !isClientReady) return

    // Use lower priority to avoid blocking UI
    const initializeFlags = async () => {
      try {
        // For mobile/low-end devices, initialize flags with very short timeouts
        const timeoutDuration = isIOSDevice || isLowEndDevice ? 500 : 1000

        // Create a timeout promise
        const timeout = (ms: number) =>
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Flag retrieval timed out')), ms)
          )

        // Initialize each flag with timeout protection
        const [newUIValue, heroTextValue, premiumValue] = await Promise.all([
          // Race each flag retrieval against timeout
          Promise.race([
            client.getBooleanValue(FeatureFlags.NEW_UI_COMPONENT, false).catch(() => false),
            timeout(timeoutDuration)
          ]).catch(() => false),

          Promise.race([
            client.getStringValue(FeatureFlags.HERO_TEXT_TEST, 'default').catch(() => 'default'),
            timeout(timeoutDuration)
          ]).catch(() => 'default'),

          Promise.race([
            client.getBooleanValue(FeatureFlags.PREMIUM_FEATURE, false).catch(() => false),
            timeout(timeoutDuration)
          ]).catch(() => false)
        ])

        // Update atoms
        setNewUIFlag(newUIValue as boolean)
        setHeroTextFlag(heroTextValue as any)
        setPremiumFlag(premiumValue as boolean)
      } catch (err) {
        isDev && console.error('Error initializing feature flags:', err)

        // Use default values if initialization fails
        setNewUIFlag(false)
        setHeroTextFlag('default')
        setPremiumFlag(false)

        // Mark initialization as failed
        setInitializationFailed(true)
      }
    }

    // Don't block UI - use setTimeout to defer flag initialization
    setTimeout(initializeFlags, 0)
  }, [client, isClientReady, setNewUIFlag, setHeroTextFlag, setPremiumFlag])

  /**
   * Check if a feature flag value is cached and valid
   */
  const getCachedValue = useCallback(<T>(flagKey: string): { value: T; found: boolean } => {
    const cached = flagResultsCache[flagKey]
    const now = Date.now()

    if (cached && now < cached.expiry) {
      return { value: cached.value as T, found: true }
    }

    return { value: null as unknown as T, found: false }
  }, [])

  /**
   * Cache a feature flag value
   */
  const cacheValue = useCallback(<T>(flagKey: string, value: T) => {
    const now = Date.now()
    flagResultsCache[flagKey] = {
      value,
      timestamp: now,
      expiry: now + CACHE_TIMEOUT
    }
  }, [])

  /**
   * Check if a feature flag is enabled
   */
  const isFeatureEnabled = useCallback(
    async (
      flagKey: string,
      defaultValue: boolean = false,
      options?: { trackAnalytics?: boolean }
    ) => {
      // First check cache for faster response
      const { value: cachedValue, found: isCached } = getCachedValue<boolean>(flagKey)
      if (isCached) {
        // Use cached value
        return cachedValue
      }

      // Check for overrides (local development, admin tools)
      if (overrides[flagKey] !== undefined) {
        // Track override usage if requested
        if (options?.trackAnalytics) {
          // Don't wait for tracking to complete
          trackFeatureFlag(flagKey, overrides[flagKey], 'impression', { overridden: true }).catch(
            () => {
              /* ignore tracking errors */
            }
          )
        }

        const value =
          typeof overrides[flagKey] === 'boolean'
            ? (overrides[flagKey] as boolean)
            : Boolean(overrides[flagKey])

        // Cache the result
        cacheValue(flagKey, value)
        return value
      }

      // If initialization failed and we're on iOS, just return default
      if (initializationFailed && (isIOSDevice || isLowEndDevice)) {
        return defaultValue
      }

      try {
        // Wait for client to be available
        if (!client) {
          return defaultValue
        }

        // On iOS or low-end devices, add a shorter timeout to prevent UI hanging
        if (isIOSDevice || isLowEndDevice) {
          const timeoutPromise = new Promise<boolean>((resolve) => {
            setTimeout(() => resolve(defaultValue), 500) // Shorter timeout (500ms)
          })

          try {
            const valuePromise = client.getBooleanValue(flagKey, defaultValue)
            const value = await Promise.race([valuePromise, timeoutPromise])

            // Track in analytics if requested, but don't wait for it
            if (options?.trackAnalytics) {
              trackFlagEvaluation(flagKey, value)
            }

            // Cache the result
            cacheValue(flagKey, value)
            return value
          } catch (error) {
            isDev &&
              console.warn(`Timeout or error evaluating feature flag ${flagKey} on mobile:`, error)
            return defaultValue
          }
        }

        // Standard evaluation for non-mobile devices
        const value = await client.getBooleanValue(flagKey, defaultValue)

        // Track in analytics if requested, but don't wait for it
        if (options?.trackAnalytics) {
          trackFlagEvaluation(flagKey, value)
        }

        // Cache the result
        cacheValue(flagKey, value)
        return value
      } catch (error) {
        isDev && console.error(`Error evaluating feature flag ${flagKey}:`, error)
        return defaultValue
      }
    },
    [overrides, client, initializationFailed, getCachedValue, cacheValue]
  )

  /**
   * Get a feature flag string value
   */
  const getStringValue = useCallback(
    async (flagKey: string, defaultValue: string = '', options?: { trackAnalytics?: boolean }) => {
      // First check cache for faster response
      const { value: cachedValue, found: isCached } = getCachedValue<string>(flagKey)
      if (isCached) {
        // Use cached value
        return cachedValue
      }

      // Check for overrides
      if (overrides[flagKey] !== undefined) {
        if (options?.trackAnalytics) {
          // Don't wait for tracking to complete
          trackFeatureFlag(flagKey, overrides[flagKey], 'impression', { overridden: true }).catch(
            () => {
              /* ignore tracking errors */
            }
          )
        }

        const value = String(overrides[flagKey])
        // Cache the result
        cacheValue(flagKey, value)
        return value
      }

      // If initialization failed and we're on mobile, just return default
      if (initializationFailed && (isIOSDevice || isLowEndDevice)) {
        return defaultValue
      }

      try {
        if (!client) {
          return defaultValue
        }

        // On mobile devices, add a shorter timeout to prevent UI hanging
        if (isIOSDevice || isLowEndDevice) {
          const timeoutPromise = new Promise<string>((resolve) => {
            setTimeout(() => resolve(defaultValue), 500)
          })

          try {
            const valuePromise = client.getStringValue(flagKey, defaultValue)
            const value = await Promise.race([valuePromise, timeoutPromise])

            if (options?.trackAnalytics) {
              trackFlagEvaluation(flagKey, value)
            }

            // Cache the result
            cacheValue(flagKey, value)
            return value
          } catch (error) {
            isDev &&
              console.warn(`Timeout or error evaluating string flag ${flagKey} on mobile:`, error)
            return defaultValue
          }
        }

        // Standard evaluation for non-mobile devices
        const value = await client.getStringValue(flagKey, defaultValue)

        if (options?.trackAnalytics) {
          trackFlagEvaluation(flagKey, value)
        }

        // Cache the result
        cacheValue(flagKey, value)
        return value
      } catch (error) {
        isDev && console.error(`Error evaluating string flag ${flagKey}:`, error)
        return defaultValue
      }
    },
    [overrides, client, initializationFailed, getCachedValue, cacheValue]
  )

  /**
   * Get a feature flag number value
   */
  const getNumberValue = useCallback(
    async (flagKey: string, defaultValue: number = 0, options?: { trackAnalytics?: boolean }) => {
      // First check cache for faster response
      const { value: cachedValue, found: isCached } = getCachedValue<number>(flagKey)
      if (isCached) {
        // Use cached value
        return cachedValue
      }

      // Check for overrides
      if (overrides[flagKey] !== undefined) {
        if (options?.trackAnalytics) {
          // Don't wait for tracking to complete
          trackFeatureFlag(flagKey, overrides[flagKey], 'impression', { overridden: true }).catch(
            () => {
              /* ignore tracking errors */
            }
          )
        }
        const value = Number(overrides[flagKey])
        const result = isNaN(value) ? defaultValue : value

        // Cache the result
        cacheValue(flagKey, result)
        return result
      }

      // If initialization failed and we're on mobile, just return default
      if (initializationFailed && (isIOSDevice || isLowEndDevice)) {
        return defaultValue
      }

      try {
        if (!client) {
          return defaultValue
        }

        // On mobile devices, add a shorter timeout to prevent UI hanging
        if (isIOSDevice || isLowEndDevice) {
          const timeoutPromise = new Promise<number>((resolve) => {
            setTimeout(() => resolve(defaultValue), 500)
          })

          try {
            const valuePromise = client.getNumberValue(flagKey, defaultValue)
            const value = await Promise.race([valuePromise, timeoutPromise])

            if (options?.trackAnalytics) {
              trackFlagEvaluation(flagKey, value)
            }

            // Cache the result
            cacheValue(flagKey, value)
            return value
          } catch (error) {
            isDev &&
              console.warn(`Timeout or error evaluating number flag ${flagKey} on mobile:`, error)
            return defaultValue
          }
        }

        // Standard evaluation for non-mobile devices
        const value = await client.getNumberValue(flagKey, defaultValue)

        if (options?.trackAnalytics) {
          trackFlagEvaluation(flagKey, value)
        }

        // Cache the result
        cacheValue(flagKey, value)
        return value
      } catch (error) {
        isDev && console.error(`Error evaluating number flag ${flagKey}:`, error)
        return defaultValue
      }
    },
    [overrides, client, initializationFailed, getCachedValue, cacheValue]
  )

  /**
   * Get a feature flag object value
   */
  const getObjectValue = useCallback(
    async <T extends object>(
      flagKey: string,
      defaultValue: T,
      options?: { trackAnalytics?: boolean }
    ): Promise<T> => {
      // First check cache for faster response
      const { value: cachedValue, found: isCached } = getCachedValue<T>(flagKey)
      if (isCached) {
        // Use cached value
        return cachedValue
      }

      // Check for overrides
      if (overrides[flagKey] !== undefined) {
        if (options?.trackAnalytics) {
          // Don't wait for tracking to complete
          trackFeatureFlag(flagKey, overrides[flagKey], 'impression', { overridden: true }).catch(
            () => {
              /* ignore tracking errors */
            }
          )
        }
        try {
          const value =
            typeof overrides[flagKey] === 'object'
              ? (overrides[flagKey] as T)
              : (JSON.parse(String(overrides[flagKey])) as T)

          // Cache the result
          cacheValue(flagKey, value)
          return value
        } catch (e) {
          return defaultValue
        }
      }

      // If initialization failed and we're on mobile, just return default
      if (initializationFailed && (isIOSDevice || isLowEndDevice)) {
        return defaultValue
      }

      try {
        if (!client) {
          return defaultValue
        }

        // On mobile devices, add a shorter timeout to prevent UI hanging
        if (isIOSDevice || isLowEndDevice) {
          const timeoutPromise = new Promise<T>((resolve) => {
            setTimeout(() => resolve(defaultValue), 500)
          })

          try {
            const valuePromise = client.getObjectValue<T>(flagKey, defaultValue)
            const value = await Promise.race([valuePromise, timeoutPromise])

            if (options?.trackAnalytics) {
              // Object values need to be stringified for analytics tracking
              trackFlagEvaluation(flagKey, JSON.stringify(value))
            }

            // Cache the result
            cacheValue(flagKey, value)
            return value
          } catch (error) {
            isDev &&
              console.warn(`Timeout or error evaluating object flag ${flagKey} on mobile:`, error)
            return defaultValue
          }
        }

        // Standard evaluation for non-mobile devices
        const value = await client.getObjectValue<T>(flagKey, defaultValue)

        if (options?.trackAnalytics) {
          // Object values need to be stringified for analytics tracking
          // because trackFlagEvaluation only accepts boolean, string, or number
          trackFlagEvaluation(flagKey, JSON.stringify(value))
        }

        // Cache the result
        cacheValue(flagKey, value)
        return value
      } catch (error) {
        isDev && console.error(`Error evaluating object flag ${flagKey}:`, error)
        return defaultValue
      }
    },
    [overrides, client, initializationFailed, getCachedValue, cacheValue]
  )

  /**
   * Override a feature flag with a specific value
   */
  const overrideFeature = useCallback(
    (flagKey: string, value: boolean | string | number | object) => {
      setOverrides((prev) => ({
        ...prev,
        [flagKey]: value
      }))

      // Also update cache
      cacheValue(flagKey, value)
    },
    [setOverrides, cacheValue]
  )

  /**
   * Reset a specific feature flag override
   */
  const resetOverride = useCallback(
    (flagKey: string) => {
      setOverrides((prev) => {
        const newOverrides = { ...prev }
        delete newOverrides[flagKey]
        return newOverrides
      })

      // Clear from cache
      delete flagResultsCache[flagKey]
    },
    [setOverrides]
  )

  /**
   * Reset all feature flag overrides
   */
  const resetOverrides = useCallback(() => {
    setOverrides({})

    // Clear cache
    Object.keys(flagResultsCache).forEach((key) => {
      delete flagResultsCache[key]
    })
  }, [setOverrides])

  // Return memoized API - prevent unnecessary rerenders
  const api = useMemo(
    () => ({
      isFeatureEnabled,
      getStringValue,
      getNumberValue,
      getObjectValue,
      overrides,
      setOverrides,
      overrideFeature,
      resetOverrides,
      resetOverride,

      // Convenience methods
      isNewUIEnabled: async (defaultValue: boolean = false) =>
        await isFeatureEnabled(FeatureFlags.NEW_UI_COMPONENT, defaultValue),

      getHeroTextVariant: async (defaultValue: HeroTextVariant = 'default') =>
        (await getStringValue(FeatureFlags.HERO_TEXT_TEST, defaultValue)) as HeroTextVariant,

      isPremiumFeatureEnabled: async (defaultValue: boolean = false) =>
        await isFeatureEnabled(FeatureFlags.PREMIUM_FEATURE, defaultValue)
    }),
    [
      isFeatureEnabled,
      getStringValue,
      getNumberValue,
      getObjectValue,
      overrides,
      setOverrides,
      overrideFeature,
      resetOverrides,
      resetOverride
    ]
  )

  return api
}
